import React, { useState, useEffect, useRef } from "react";
import { FiPlus } from "react-icons/fi";
import { DeviceInfo } from "../../services/vision/types";
import { ErrorMessage } from "../ErrorMessage";
import { FieldItem } from "./FieldItem";
import { ConfidenceIndicator } from "./ConfidenceIndicator";
import { CustomFieldForm } from "./CustomFieldForm";
import {
  DeviceInfoHeader,
  AddFieldButton,
  CustomFieldContainer,
  SpecsList,
  SpecItem,
  ResultItem,
  ResultLabel,
  ResultValue,
} from "./styles";
import {
  createFieldLabels,
  excludeFields,
  getFieldLabel,
  formatFieldKey,
} from "./utils";

interface DeviceInfoDisplayProps {
  deviceInfo: DeviceInfo;
  isAIEnhanced?: boolean;
  onReset: () => void;
  onUpdate?: (updatedInfo: DeviceInfo) => void;
  defaultFields?: Record<string, string>;
}

export const DeviceInfoDisplay: React.FC<DeviceInfoDisplayProps> = ({
  deviceInfo,
  isAIEnhanced = true,
  onReset,
  onUpdate,
  defaultFields,
}) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedInfo, setEditedInfo] = useState<
    DeviceInfo & Record<string, any>
  >({ ...deviceInfo });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const [isAddingCustomField, setIsAddingCustomField] = useState(false);
  const [customFieldName, setCustomFieldName] = useState("");
  const [customFieldValue, setCustomFieldValue] = useState("");
  const [customFields, setCustomFields] = useState<
    { key: string; label: string; value: string }[]
  >([]);

  const { confidence } = deviceInfo;
  const confidenceValue = confidence ?? 0;
  const fieldLabels = React.useMemo(() => createFieldLabels(), []);

  // Fields that should always be shown at the top
  const priorityFields = ["title"];

  useEffect(() => {
    // Apply default fields if provided and the corresponding fields are empty
    const initialInfo = { ...deviceInfo } as Record<string, any>;

    if (defaultFields) {
      Object.entries(defaultFields).forEach(([key, value]) => {
        // Only set default if the field doesn't exist or is empty
        if (!initialInfo[key] || initialInfo[key] === "") {
          initialInfo[key] = value;
        }
      });
    }

    setEditedInfo(initialInfo);
  }, [deviceInfo, defaultFields]);

  useEffect(() => {
    if (editingField && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingField]);

  const handleChange = (field: string, value: string) => {
    setEditedInfo((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleStartEditing = (field: string) => {
    if (!onUpdate) return;
    setEditingField(field);
  };

  const handleSaveField = (field: string) => {
    // Validate required fields
    if (field === "title") {
      if (!editedInfo.title || editedInfo.title.trim() === "") {
        setErrors({ title: "Title is required" });
        return;
      }
    }
    if (onUpdate) {
      onUpdate(editedInfo);
    }

    setEditingField(null);
  };

  const handleCancelEditing = () => {
    setEditedInfo({ ...deviceInfo });
    setErrors({});
    setEditingField(null);
  };

  const handleAddCustomField = () => {
    setIsAddingCustomField(true);
  };

  const handleSaveCustomField = () => {
    if (!customFieldName.trim()) {
      setErrors({ ...errors, customFieldName: "Field name is required" });
      return;
    }

    // Format the field name to be JSON-compatible in camelCase
    const key = formatFieldKey(customFieldName);

    setCustomFields([
      ...customFields,
      {
        key,
        label: customFieldName,
        value: customFieldValue,
      },
    ]);

    const updatedInfo = {
      ...editedInfo,
      [key]: customFieldValue,
    };

    setEditedInfo(updatedInfo);

    if (onUpdate) {
      onUpdate(updatedInfo as DeviceInfo);
    }

    setCustomFieldName("");
    setCustomFieldValue("");
    setIsAddingCustomField(false);
    setErrors({});
  };

  const handleCancelCustomField = () => {
    setCustomFieldName("");
    setCustomFieldValue("");
    setIsAddingCustomField(false);
    setErrors({});
  };

  const handleDeleteField = (key: string, isCustomField: boolean = false) => {
    let updatedInfo = { ...editedInfo } as Record<string, any>;
    delete updatedInfo[key];

    if (isCustomField) {
      setCustomFields(customFields.filter((field) => field.key !== key));
    }

    setEditedInfo(updatedInfo);

    if (onUpdate) {
      onUpdate(updatedInfo as DeviceInfo);
    }
  };

  const hasMeaningfulInfo =
    Object.keys(deviceInfo).filter(
      (key) => !["confidence", "aiEnhanced", "error"].includes(key)
    ).length > 0;

  // Define which fields are required
  const isRequired = (key: string): boolean => key === "title";

  // Get default field keys if they exist
  const defaultFieldKeys = defaultFields ? Object.keys(defaultFields) : [];

  // Use editedInfo instead of deviceInfo to include default fields
  const populatedFields = Object.entries(editedInfo)
    .filter(
      ([key, value]) =>
        !excludeFields.includes(key) &&
        !customFields.some((field) => field.key === key) &&
        !priorityFields.includes(key) &&
        // Show the field if it has a value OR if it's in defaultFields
        ((value !== undefined && value !== null && value !== "") ||
          defaultFieldKeys.includes(key))
    )
    .map(([key]) => key)
    .sort();

  const sortedFields = [...priorityFields, ...populatedFields];

  return (
    <>
      <DeviceInfoHeader>Information</DeviceInfoHeader>
      {isAIEnhanced && <ConfidenceIndicator confidence={confidenceValue} />}

      {!hasMeaningfulInfo && (
        <ErrorMessage
          title=""
          message="No device information detected. Try scanning again with a clearer image or different angle."
        />
      )}

      {sortedFields.map((key) => {
        // Skip osVersion as it's handled with osName
        if (key === "osVersion") return null;

        const value = editedInfo[key as keyof DeviceInfo];

        return (
          <FieldItem
            key={key}
            fieldKey={key}
            value={value}
            deviceInfo={deviceInfo}
            editingField={editingField}
            isRequired={isRequired(key)}
            fieldLabels={fieldLabels}
            errors={errors}
            onStartEditing={handleStartEditing}
            onSaveField={handleSaveField}
            onCancelEditing={handleCancelEditing}
            onChange={handleChange}
            onDelete={handleDeleteField}
          />
        );
      })}

      {/* Other details section */}
      {!editingField &&
        deviceInfo.otherDetails &&
        deviceInfo.otherDetails["specs"] &&
        Array.isArray(deviceInfo.otherDetails["specs"]) &&
        deviceInfo.otherDetails["specs"].length > 0 && (
          <ResultItem>
            <ResultLabel>Specifications</ResultLabel>
            <ResultValue>
              <SpecsList>
                {(deviceInfo.otherDetails["specs"] as string[]).map(
                  (spec: string, index: number) => (
                    <SpecItem key={index}>{spec}</SpecItem>
                  )
                )}
              </SpecsList>
            </ResultValue>
          </ResultItem>
        )}

      {!editingField &&
        deviceInfo.otherDetails &&
        Object.keys(deviceInfo.otherDetails).filter((key) => key !== "specs")
          .length > 0 && (
          <ResultItem>
            <ResultLabel>Other Details</ResultLabel>
            <ResultValue>
              <SpecsList>
                {Object.entries(deviceInfo.otherDetails)
                  .filter(([key]) => key !== "specs")
                  .map(([key, value], index) => (
                    <SpecItem key={index}>
                      <strong>{getFieldLabel(key, fieldLabels)}:</strong>{" "}
                      {value}
                    </SpecItem>
                  ))}
              </SpecsList>
            </ResultValue>
          </ResultItem>
        )}

      {/* Custom fields section */}
      {customFields.length > 0 && (
        <CustomFieldContainer>
          {customFields.map((field) => (
            <FieldItem
              key={field.key}
              fieldKey={field.key}
              value={editedInfo[field.key]}
              deviceInfo={deviceInfo}
              editingField={editingField}
              fieldLabels={{ [field.key]: field.label }}
              errors={errors}
              onStartEditing={handleStartEditing}
              onSaveField={handleSaveField}
              onCancelEditing={handleCancelEditing}
              onChange={handleChange}
              onDelete={handleDeleteField}
              isCustomField={true}
            />
          ))}
        </CustomFieldContainer>
      )}

      {/* Add custom field form */}
      {isAddingCustomField ? (
        <CustomFieldForm
          customFieldName={customFieldName}
          customFieldValue={customFieldValue}
          errors={errors}
          onNameChange={setCustomFieldName}
          onValueChange={setCustomFieldValue}
          onSave={handleSaveCustomField}
          onCancel={handleCancelCustomField}
        />
      ) : (
        onUpdate && (
          <AddFieldButton onClick={handleAddCustomField}>
            <FiPlus size={16} /> Add Custom Field
          </AddFieldButton>
        )
      )}
    </>
  );
};
